import { z } from "zod";
import { mediaListSchema } from "../common.js";

export const VT_GROUND_TYPES = [
  "terre_vegetale",
  "terre_argileuse",
  "terre_rocailleuse",
  "terre_sablonneuse",
  "beton",
  "bitume",
  "sable",
  "mixte",
  "autre",
] as const;
export type VtGroundType = (typeof VT_GROUND_TYPES)[number];

/** Libellés FR, mot pour mot ceux de l'app source (NATURE_SOL_OPTIONS). */
export const VT_GROUND_TYPE_LABELS: Record<VtGroundType, string> = {
  terre_vegetale: "Terre végétale",
  terre_argileuse: "Terre argileuse",
  terre_rocailleuse: "Terre rocailleuse",
  terre_sablonneuse: "Terre sablonneuse",
  beton: "Béton",
  bitume: "Bitume",
  sable: "Sable",
  mixte: "Mixte",
  autre: "Autre",
};

export const VT_FOUNDATION_TYPES = [
  "weasyfix_75",
  "weasyfix_120",
  "platines_pieux",
  "platines_goujons",
  "dalle_beton_isole",
  "weasyfix_sable",
  "technopieux",
  "vide_sanitaire_semelle_filante",
  "vide_sanitaire_maconnerie_plancher_bois",
  "autre",
] as const;
export type VtFoundationType = (typeof VT_FOUNDATION_TYPES)[number];

/**
 * Libellés FR, mot pour mot ceux de l'app source (FONDATION_OPTIONS),
 * y compris « Weasifix spécial sable » (orthographe source) + option Autre.
 * « Vide sanitaire maçonné » = solution B de la matrice PDF (semelles
 * filantes + soubassement maçonné + plancher bois isolé).
 */
export const VT_FOUNDATION_TYPE_LABELS: Record<VtFoundationType, string> = {
  weasyfix_75: "Weasyfix 75",
  weasyfix_120: "Weasyfix 120",
  platines_pieux: "Platines avec pieux d'ancrage",
  platines_goujons: "Platines avec goujons pour dalle béton",
  dalle_beton_isole: "Dalle béton isolé",
  weasyfix_sable: "Weasifix spécial sable",
  technopieux: "Technopieux",
  vide_sanitaire_semelle_filante: "Vide sanitaire sur semelle filante",
  vide_sanitaire_maconnerie_plancher_bois: "Vide sanitaire maçonné",
  autre: "Autre",
};

export const VT_SLOPE_CATEGORIES = [
  "nulle",
  "legere",
  "moyenne",
  "forte",
  "tres_forte",
] as const;
export type VtSlopeCategory = (typeof VT_SLOPE_CATEGORIES)[number];

export const VT_SLOPE_CATEGORY_LABELS: Record<VtSlopeCategory, string> = {
  nulle: "Nulle",
  legere: "Légère",
  moyenne: "Moyenne",
  forte: "Forte",
  tres_forte: "Très forte",
};

export const VT_REQUIRED_MACHINES = [
  "minipelle",
  "trancheuse",
  "manuscopique",
  "echafaudage",
  "levage",
  "container",
  "autres",
] as const;
export type VtRequiredMachine = (typeof VT_REQUIRED_MACHINES)[number];

export const VT_REQUIRED_MACHINE_LABELS: Record<VtRequiredMachine, string> = {
  minipelle: "Mini-pelle",
  trancheuse: "Trancheuse",
  manuscopique: "Manuscopique",
  echafaudage: "Échafaudage",
  levage: "Levage",
  container: "Container",
  autres: "Autres",
};

export const VT_PARCELLE_NUE_BESOINS = [
  "wcChantier",
  "baseVie",
  "container",
  "autres",
] as const;
export type VtParcelleNueBesoin = (typeof VT_PARCELLE_NUE_BESOINS)[number];

export const vtTerrainSchema = z
  .object({
    // Implantation photos
    photoNorth: mediaListSchema.optional(),
    photoSouth: mediaListSchema.optional(),
    photoEast: mediaListSchema.optional(),
    photoWest: mediaListSchema.optional(),
    // Sol & fondation
    groundType: z.enum(VT_GROUND_TYPES).optional(),
    groundTypeAutreDetail: z.string().optional(),
    foundationType: z.enum(VT_FOUNDATION_TYPES).optional(),
    foundationAutreDetail: z.string().optional(),
    foundationOverrideReason: z.string().optional(),
    soilPhoto: mediaListSchema.optional(),
    // Pente
    slopeCategory: z.enum(VT_SLOPE_CATEGORIES).optional(),
    slopePhoto: mediaListSchema.optional(),
    // Décaissé
    isExcavationRequired: z.boolean().optional(),
    excavationDoneByClient: z.boolean().optional(),
    // Stockage
    storagePhoto: mediaListSchema.optional(),
    storageSolution: z.string().optional(),
    // Engins
    needMachines: z.boolean().optional(),
    requiredMachines: z.array(z.enum(VT_REQUIRED_MACHINES)).optional(),
    requiredMachinesAutresDetail: z.string().optional(),
    // Parcelle nue
    parcelleNue: z.boolean().optional(),
    parcelleNueBesoins: z.array(z.enum(VT_PARCELLE_NUE_BESOINS)).optional(),
    parcelleNueAutresDetail: z.string().optional(),
    // Remarks
    terrainRemarks: z.string().optional(),
  })
  .partial();

export type VtTerrain = z.infer<typeof vtTerrainSchema>;
